import React from "react";
import { motion } from "framer-motion";
import { FaCheck, FaArrowRight } from "react-icons/fa6";
import Button from "../../../components/ui/Button";
import { pricingPlans } from "./data";

const Pricing: React.FC = () => {
  const handleContactUs = () => {
    window.open(
      "https://cal.com/seamlessvisa/consultation",
      "_blank",
    );
  };

  // Restore original pricing content
  const tiers = [
    {
      ...pricingPlans[0],
      price: "$199",
      subtext: "Per 10 applications",
      description: "Everything necessary to get started.",
    },
    {
      ...pricingPlans[1],
      price: "$499",
      subtext: "Per 10 applications",
      description:
        "Everything in Standard, plus essential tools for growing your business.",
    },
    {
      ...pricingPlans[2],
      price: "Contact Us",
      subtext: "Tailored solutions",
      description:
        "Everything in Premium, plus collaboration tools and deeper insights.",
    },
  ];

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl font-bold font-spartan text-black-1a leading-tight">
            Simple,{" "}
            <span className="text-blue-00 italic mx-1">transparent</span>{" "}
            pricing
          </h2>
          <p className="mt-6 text-xl text-dark-6b font-inter">
            Choose the package that best fits your business needs. No hidden
            fees.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {tiers.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`relative flex flex-col p-8 rounded-3xl border transition-all duration-300 ${
                plan.isHighlighted
                  ? "bg-blue-02 text-white border-blue-02 shadow-2xl md:scale-105 z-10"
                  : "bg-white text-black-1a border-gray-100 hover:border-blue-200 shadow-xl"
              }`}
            >
              {plan.isHighlighted && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-00 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg">
                  Most Popular
                </div>
              )}

              <div className="mb-8">
                <h3
                  className={`text-2xl font-bold font-spartan mb-2 ${
                    plan.isHighlighted ? "text-white" : "text-black-1a"
                  }`}
                >
                  {plan.title}
                </h3>
                <div className="flex items-baseline gap-1 mt-4">
                  <span className="text-4xl font-bold font-spartan">
                    {plan.price}
                  </span>
                  {plan.price !== "Contact Us" && (
                    <span
                      className={`text-sm font-semibold ${
                        plan.isHighlighted ? "text-blue-200" : "text-dark-6b"
                      }`}
                    >
                      /month
                    </span>
                  )}
                </div>
                <p
                  className={`mt-2 text-sm font-medium ${
                    plan.isHighlighted ? "text-blue-100" : "text-dark-6b"
                  }`}
                >
                  {plan.subtext}
                </p>
                <p
                  className={`mt-6 text-sm leading-relaxed ${
                    plan.isHighlighted ? "text-blue-50" : "text-dark-6b"
                  }`}
                >
                  {plan.description}
                </p>
              </div>

              <ul className="flex-1 space-y-4 mb-10">
                {plan.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-start gap-3">
                    <div
                      className={`mt-1 shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                        plan.isHighlighted
                          ? "bg-blue-400/20 text-blue-200"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      <FaCheck className="w-3 h-3" />
                    </div>
                    <span
                      className={`text-sm ${
                        plan.isHighlighted ? "text-blue-50" : "text-dark-6b"
                      }`}
                    >
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="flex justify-center">
                <Button
                  label={plan.buttonText}
                  onClick={handleContactUs}
                  variant={plan.isHighlighted ? "primary" : "secondary"}
                  size="large"
                  endIcon={<FaArrowRight />}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
